import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@yhelp/ui';
import { useAuth } from '@/auth';
import { formatAuth0Error } from '@/auth/auth0-errors';
import { useLogin } from '@/auth/login';

export interface LoginButtonProps {
  size?: 'sm' | 'md' | 'lg';
  loginLabel?: string;
  logoutLabel?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  className?: string;
}

export function LoginButton({
  size = 'sm',
  loginLabel = 'Login',
  logoutLabel = 'Sair',
  variant,
  className,
}: LoginButtonProps) {
  const { logout } = useAuth();
  const { login, isAuthenticated, isLoading, error } = useLogin();
  const [clickError, setClickError] = useState<string | null>(null);
  const displayError = clickError ?? (error ? formatAuth0Error(error) : null);

  if (isLoading) {
    return (
      <Button size={size} variant={variant ?? 'secondary'} disabled aria-busy>
        Carregando…
      </Button>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/app">
          <Button size={size} variant={variant ?? 'primary'} className={className}>
            Ir para o app
          </Button>
        </Link>
        <Button
          size={size}
          variant="ghost"
          onClick={() => void logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          {logoutLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-stretch gap-1">
      <Button
        size={size}
        variant={variant ?? 'primary'}
        className={className}
        onClick={() => {
          setClickError(null);
          void login('/app').catch((err: unknown) => {
            console.error('[yhelp] loginWithRedirect failed', err);
            setClickError(formatAuth0Error(err));
          });
        }}
      >
        {loginLabel}
      </Button>
      {displayError && (
        <p className="max-w-xs text-xs text-priority-critico" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
